<template>
  <NiceView
    :aboveTitle="generalStore.viewHeader"
    :title="CommonService.routes.DASHBOARD.title"
    :icon="CommonService.routes.DASHBOARD.icon"
    :loading="loading"
    flexBody
    class="dashboard-view"
  >
    <div class="cards">
      <!--------------------------- Guest count --------------------------->
      <div class="card-data color1">
        <div class="title" v-translate>Guest count</div>
        <div class="value" v-if="!loading">{{ stats.guest_active_count || 0 }}</div>
        <NiceLoading color="white" v-if="loading" />
      </div>

      <!--------------------------- Checked in guests --------------------------->
      <div class="card-data color2">
        <div class="title" v-translate>Checked in guests</div>
        <div class="value" v-if="!loading">{{ stats.guest_checked_in_count || 0 }}</div>
        <NiceLoading color="white" v-if="loading" />
      </div>

      <!--------------------------- Plus one count --------------------------->
      <div class="card-data color3" v-if="modules.PLUS_ONES">
        <div class="title" v-translate>Plus one count</div>
        <div class="value" v-if="!loading">{{ stats.guest_plus_one_count || 0 }}</div>
        <NiceLoading color="white" v-if="loading" />
      </div>

      <!--------------------------- Checked in plus one count --------------------------->
      <div class="card-data color4" v-if="modules.PLUS_ONES">
        <div class="title" v-translate>Checked in +1</div>
        <div class="value" v-if="!loading">{{ stats.guest_plus_one_check_in_count || 0 }}</div>
        <NiceLoading color="white" v-if="loading" />
      </div>
    </div>

    <NiceWrapper :title="$gettext('Groups')">
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th v-translate>Name</th>
              <th class="text-right" v-translate>Guest count</th>
              <th class="text-right" v-if="modules.PLUS_ONES" v-translate>Plus one count</th>
              <th class="text-right" v-translate>Checked in count</th>
              <th class="text-right" v-if="modules.PLUS_ONES" v-translate>Checked in plus ones count</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in stats.guest_groups" :key="group.id">
              <td>{{ group.name }}</td>
              <td class="text-right">{{ group.guest_active_count || "-" }}</td>
              <td class="text-right" v-if="modules.PLUS_ONES">{{ group.guest_plus_one_count || "-" }}</td>
              <td class="text-right">{{ group.guest_checked_in_count || "-" }}</td>
              <td class="text-right" v-if="modules.PLUS_ONES">{{ group.guest_plus_one_check_in_count || "-" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <NiceLoading v-if="loading" class="nice-table-loading"></NiceLoading>
      <NiceNoData
        v-if="stats && stats.guest_groups && stats.guest_groups.length == 0"
        class="nice-table-loading"
        :message="$gettext('No statistics yet')"
      ></NiceNoData>
    </NiceWrapper>

    <!-- <pre>{{ stats }}</pre> -->
  </NiceView>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";

import CommonService from "@/services/common.service";
import ApiService from "@/services/api/api.service";
import ErrorService from "@/services/error.service";

import { useRoute } from "vue-router";
const route = useRoute();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const modules = computed(() => generalStore.modules);

const stats = ref({});
const loading = ref(true);

watch(
  () => route.params,
  (oldValue, newValue) => {
    if (oldValue.eventId != newValue.eventId) getData();
  }
);

async function getData() {
  const organizationId = route.params.organizationId;
  const eventId = route.params.eventId;
  loading.value = true;
  try {
    stats.value = await ApiService.events.stats(organizationId, eventId);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  getData();
});
</script>

<style lang="scss" scoped>
.dashboard-view {
  .nice-table-loading {
    border: 1px solid var(--border-color);
    border-top: 0 none;
    width: 100%;
    padding: 3rem;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    vertical-align: middle;
    min-height: 41px;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);

    thead {
      tr {
        th {
          text-align: left;
          text-transform: uppercase;
          font-size: 0.8em;
          font-weight: 600;
          padding: 12px 10px;
          background: var(--nice-hover-color);
          border-bottom: 1px solid var(--border-color);
        }
      }
    }

    tbody {
      tr {
        td {
          padding: 12px 10px;
          background: var(--nice-card-bg);
          border-top: 1px solid var(--border-color);
        }
      }
    }
  }

  .cards {
    display: grid;
    gap: 2rem;
    margin-bottom: 2rem;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));

    .card-data {
      background: var(--card-bg);
      border-radius: var(--border-radius-large);
      border: 1px solid var(--border-color);
      padding: 2rem;
      box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.05);
      text-align: center;
      line-height: 1;

      .title {
        font-weight: bold;
        margin-bottom: 0.5rem;
      }

      .value {
        font-size: 4em;
      }

      .nice-loading {
        height: 56px;

        .nice-loading-wrapper {
          height: 35px;
          width: 35px;
        }
      }
    }

    @media (max-width: 576px) {
      grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
      gap: 1rem;
      margin-bottom: 1rem;

      .card-data {
        .title {
          font-size: 0.7em;
        }

        .value {
          font-size: 2em;
        }
      }
    }
  }
}
</style>
